export const SELECTABLE_CAMPAIGN_FEATURE_DEFINITIONS = Object.freeze([
  { key: 'crafting', label: 'Crafting', desc: 'Recipes, materials, and the bench' },
  { key: 'professions', label: 'Professions', desc: 'Trade skills with their own progression' },
  { key: 'cinematics', label: 'Cinematics', desc: 'DM-scripted scene moments with effects' },
  {
    key: 'combat',
    label: 'Combat',
    desc: 'Real-time turn-based combat with auto-hooked spells and abilities',
  },
  { key: 'systems', label: 'Systems', desc: 'House rules engine for custom mechanics' },
  { key: 'shops', label: 'Shops', desc: 'Merchants, inventories, haggling' },
  { key: 'loot', label: 'Loot', desc: 'Drop tables and treasure distribution' },
  { key: 'travelLog', label: 'Travel Log', desc: 'Group travel history and survival pacing' },
]);

export const SELECTABLE_CAMPAIGN_FEATURE_KEYS = Object.freeze(
  SELECTABLE_CAMPAIGN_FEATURE_DEFINITIONS.map((feature) => feature.key),
);

export const SELECTABLE_CAMPAIGN_FEATURE_KEY_SET = new Set(SELECTABLE_CAMPAIGN_FEATURE_KEYS);

export const CAMPAIGN_FEATURE_FLAG_KEYS = Object.freeze({
  crafting: 'feature.crafting',
  professions: 'feature.professions',
  cinematics: 'feature.cinematics',
  combat: 'feature.combat',
  systems: 'feature.systems',
  shops: 'feature.shops',
  loot: 'feature.loot',
  travelLog: 'feature.travelLog',
});

export const ALWAYS_ENABLED_CAMPAIGN_FEATURES = Object.freeze({
  auditLog: true,
});

export const CAMPAIGN_HOMEBREW_KEYS = Object.freeze([
  'durability',
  'foodSpoilage',
  'hungerStarvation',
  'diets',
  'diseases',
  'mana',
]);

const extractEnabledFeatures = (campaignOrFeatures) => {
  if (!campaignOrFeatures || typeof campaignOrFeatures !== 'object') return null;
  if (
    Object.prototype.hasOwnProperty.call(campaignOrFeatures, 'enabledFeatures')
    && campaignOrFeatures.enabledFeatures
    && typeof campaignOrFeatures.enabledFeatures === 'object'
  ) {
    return campaignOrFeatures.enabledFeatures;
  }
  return campaignOrFeatures;
};

const isGlobalFlagEnabled = (globalFlags, featureKey) => {
  if (!globalFlags || typeof globalFlags !== 'object') return true;
  const flagKey = CAMPAIGN_FEATURE_FLAG_KEYS[featureKey] || `feature.${featureKey}`;
  const flag = globalFlags[flagKey];
  if (!flag || typeof flag !== 'object') return true;
  return flag.enabled !== false;
};

export const getEffectiveCampaignFeatures = (campaignOrFeatures, globalFlags = null) => {
  const enabledFeatures = extractEnabledFeatures(campaignOrFeatures);
  const out = {};

  for (const key of SELECTABLE_CAMPAIGN_FEATURE_KEYS) {
    const campaignAllows = !enabledFeatures
      || typeof enabledFeatures !== 'object'
      || enabledFeatures[key] !== false;
    out[key] = campaignAllows && isGlobalFlagEnabled(globalFlags, key);
  }

  if (!out.crafting) {
    out.professions = false;
  }

  return out;
};

export const isCampaignFeatureEnabled = (features, key) => {
  if (key === 'auditLog') return true;
  if (!SELECTABLE_CAMPAIGN_FEATURE_KEY_SET.has(key)) return true;
  return getEffectiveCampaignFeatures(features)[key] !== false;
};

export const getEffectiveCampaignHomebrew = (homebrew) => {
  if (!homebrew || typeof homebrew !== 'object') {
    return {
      ...Object.fromEntries(CAMPAIGN_HOMEBREW_KEYS.map((key) => [key, true])),
      foodSettings: true,
    };
  }

  const out = Object.fromEntries(
    CAMPAIGN_HOMEBREW_KEYS.map((key) => [
      key,
      Object.prototype.hasOwnProperty.call(homebrew, key) ? homebrew[key] === true : false,
    ]),
  );

  if (!out.hungerStarvation) {
    out.diets = false;
  }

  out.foodSettings = !!(out.foodSpoilage || out.hungerStarvation || out.diets);
  return out;
};
